/**
 * Passkey (WebAuthn) authentication service
 * Handles passkey registration and authentication flows
 */

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
} from "@simplewebauthn/browser";
import type {
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { getSdk } from "@/composables/useSdk";
import { authService } from "./authService";
import type { AuthTokens, AuthUser } from "@/types";
import type { Passkey } from "@cc-consulting-nv/ccsdk";

// Re-export Passkey type for consumers
export type { Passkey };

class PasskeyService {
  // AbortController for conditional UI - allows cancellation
  private conditionalAbortController: AbortController | null = null;

  /**
   * Check if the browser supports WebAuthn/passkeys
   */
  isSupported(): boolean {
    return browserSupportsWebAuthn();
  }

  /**
   * Check if the browser supports WebAuthn autofill (conditional UI)
   * This allows passkey login via the browser's native autofill UI
   */
  async supportsAutofill(): Promise<boolean> {
    return browserSupportsWebAuthnAutofill();
  }

  /**
   * Cancel any ongoing conditional UI authentication
   */
  cancelConditionalUI(): void {
    if (this.conditionalAbortController) {
      this.conditionalAbortController.abort();
      this.conditionalAbortController = null;
    }
  }

  /**
   * Check if a user has any registered passkeys
   * @param email - User's email address
   */
  async checkHasPasskeys(email: string): Promise<boolean> {
    try {
      const sdk = getSdk();
      const response = await sdk.passkeyCheckHasPasskeys(email);
      return response.hasPasskeys;
    } catch (error) {
      console.error("Failed to check passkeys:", error);
      return false;
    }
  }

  /**
   * Start conditional UI (autofill) authentication
   * This shows available passkeys in the browser's autofill dropdown
   * when the user focuses on an input with autocomplete="username webauthn"
   *
   * @param onSuccess - Callback when authentication succeeds
   * @param onError - Callback when authentication fails
   * @returns Promise that resolves when conditional UI is ready
   */
  async startConditionalUI(
    onSuccess: (result: { tokens: AuthTokens; user: AuthUser }) => void,
    onError?: (error: Error) => void,
  ): Promise<void> {
    // Check if conditional UI is supported
    const supportsAutofill = await this.supportsAutofill();
    if (!supportsAutofill) {
      console.log("Conditional UI (autofill) not supported by this browser");
      return;
    }

    // Cancel any existing conditional UI request
    this.cancelConditionalUI();

    // Create new abort controller
    this.conditionalAbortController = new AbortController();

    try {
      const sdk = getSdk();

      // Get authentication options from server (no email = discoverable credentials)
      const optionsResponse = await sdk.passkeyGetAuthenticateOptions();

      const { options, sessionId } = optionsResponse;

      // Start conditional UI authentication (non-blocking)
      // This will show passkeys in the browser's autofill when user focuses input
      const authenticationResponse = await startAuthentication({
        optionsJSON: options as PublicKeyCredentialRequestOptionsJSON,
        useBrowserAutofill: true,
      });

      // If we get here, user selected a passkey from autofill
      // Verify with server and get tokens
      const authResponse = await sdk.passkeyAuthenticate(sessionId, authenticationResponse);

      // Convert response to AuthTokens format
      const tokens: AuthTokens = {
        accessToken: authResponse.accessToken,
        refreshToken: authResponse.refreshToken,
      };

      // Persist the authenticated session through the SDK.
      await sdk.setSession(tokens);

      // Fetch user profile
      const user = await authService.fetchCurrentUser();

      // Call success callback
      onSuccess({ tokens, user });
    } catch (error) {
      // Ignore AbortError (user cancelled or navigated away)
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }

      // Don't log NotAllowedError - this happens when user doesn't select a passkey
      if (error instanceof Error && error.name === "NotAllowedError") {
        return;
      }

      console.error("Conditional UI authentication failed:", error);
      if (onError && error instanceof Error) {
        onError(error);
      }
    } finally {
      this.conditionalAbortController = null;
    }
  }

  /**
   * Register a new passkey for the authenticated user
   * @param name - Friendly name for the passkey (e.g., "iPhone 15", "MacBook Pro")
   */
  async registerPasskey(name: string): Promise<Passkey> {
    const sdk = getSdk();

    // Step 1: Get registration options from server
    const optionsResponse = await sdk.passkeyGetRegisterOptions(name);

    const options = optionsResponse.options;

    // Step 2: Create credential using browser WebAuthn API
    let registrationResponse: RegistrationResponseJSON;
    try {
      registrationResponse = await startRegistration({ optionsJSON: options as PublicKeyCredentialCreationOptionsJSON });
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          throw new Error("Passkey registration was cancelled or timed out");
        }
        if (error.name === "InvalidStateError") {
          throw new Error("This passkey is already registered");
        }
      }
      throw error;
    }

    // Step 3: Send credential to server for verification and storage
    const registerResponse = await sdk.passkeyRegister(registrationResponse);

    return registerResponse.passkey;
  }

  /**
   * Authenticate with a passkey
   * @param email - Optional email to limit which passkeys are accepted
   * @returns Auth tokens and user data
   */
  async authenticateWithPasskey(email?: string): Promise<{
    tokens: AuthTokens;
    user: AuthUser;
  }> {
    const sdk = getSdk();

    // Step 1: Get authentication options from server
    const optionsResponse = await sdk.passkeyGetAuthenticateOptions(email);

    const { options, sessionId } = optionsResponse;

    // Step 2: Get assertion from authenticator
    let authenticationResponse: AuthenticationResponseJSON;
    try {
      authenticationResponse = await startAuthentication({ optionsJSON: options as PublicKeyCredentialRequestOptionsJSON });
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          throw new Error("Passkey authentication was cancelled or timed out");
        }
        if (error.name === "SecurityError") {
          throw new Error("Passkey authentication is not allowed from this origin");
        }
      }
      throw error;
    }

    // Step 3: Verify assertion with server and get tokens
    const authResponse = await sdk.passkeyAuthenticate(sessionId, authenticationResponse);

    // Convert response to AuthTokens format
    const tokens: AuthTokens = {
      accessToken: authResponse.accessToken,
      refreshToken: authResponse.refreshToken,
    };

    // Persist the authenticated session through the SDK.
    await sdk.setSession(tokens);

    // Fetch user profile
    const user = await authService.fetchCurrentUser();

    return { tokens, user };
  }

  /**
   * List all passkeys for the authenticated user
   */
  async listPasskeys(): Promise<Passkey[]> {
    const sdk = getSdk();
    return sdk.passkeyList();
  }

  /**
   * Update a passkey's name
   * @param id - Passkey ID
   * @param name - New name for the passkey
   */
  async renamePasskey(id: string, name: string): Promise<Passkey> {
    const sdk = getSdk();
    const response = await sdk.passkeyRename(id, name);
    return response.passkey;
  }

  /**
   * Delete a passkey
   * @param id - Passkey ID to delete
   */
  async deletePasskey(id: string): Promise<void> {
    const sdk = getSdk();
    await sdk.passkeyDelete(id);
  }
}

// Export singleton
export const passkeyService = new PasskeyService();
export default passkeyService;
